import { useMsal } from '@azure/msal-react'
import { mdiAccount, mdiAccountSupervisor } from '@mdi/js'
import Icon from '@mdi/react'
import { useTranslation } from 'react-i18next'

import { AccordionItem, AccordionItemContent, AccordionItemTrigger, AccordionRoot } from '@/components/ui/accordion'
import { Flex } from '@/components/ui/flex'
import { Text } from '@/components/ui/typography'
import useColorModeValue from '@/hooks/useColorModeValue'
import { ICompliance } from '@/types/tasksApprovals'
import { deepClone } from '@/utils'

import TasksList from './TasksList'

interface IProps {
    tasks: ICompliance[]
}

const Container = ({ tasks }: IProps) => {
    const { t } = useTranslation()
    const { accounts } = useMsal()
    const textColor = useColorModeValue('rgba(84, 79, 64, 0.7)', 'rgba(255, 255, 255, 0.7)')
    // const arrowColor = useColorModeValue('var(--chakra-colors-dark-700)', 'var(--chakra-colors-white)')
    const mud_id: string = (accounts[0].idTokenClaims?.UID as string) || ''

    const tasksGroupedByMudId = tasks.reduce((group: Record<string, ICompliance[]>, task) => {
        const { mudid } = task
        group[mudid] = group[mudid] ?? []
        group[mudid].push(task)
        return group
    }, {})

    const spoofUser = localStorage.getItem('spoofUser')
    const mudIds = Object.keys(tasksGroupedByMudId)
    const currentUserId = spoofUser?.toLowerCase() || mud_id.toLowerCase()

    // Move current user tasks first
    const foundIdx = mudIds.findIndex((el) => el.toLowerCase() === currentUserId)
    if (foundIdx >= 0) {
        mudIds.splice(foundIdx, 1)
        mudIds.unshift(currentUserId)
    }

    // Extract current user tasks
    const currUserTasks = tasksGroupedByMudId[currentUserId] ? deepClone(tasksGroupedByMudId[currentUserId]) : []
    // Remove current user tasks from the initial object
    delete tasksGroupedByMudId[currentUserId]
    // The remaining tasks are for directs
    const othersTasks = deepClone(tasksGroupedByMudId)
    // Count directs tasks
    let othersTasksTotal = 0
    Object.keys(othersTasks).map((mudId) => {
        const tasks = othersTasks[mudId]
        othersTasksTotal += tasks.length
    })

    const getTaskInfo = (mudId: string, tasks: ICompliance[]) => {
        const icon = mudId === currentUserId ? mdiAccount : mdiAccountSupervisor
        const name = mudId === currentUserId ? 'YOU HAVE' : `${tasks[0].name.toUpperCase()} HAS`
        const nrOfTasks = tasks.length
        const title = `${name} ${nrOfTasks} ${nrOfTasks === 1 ? 'TASK' : 'TASKS'}`

        return {
            icon,
            name,
            nrOfTasks,
            title,
        }
    }

    const getCurrUserTasks = () => {
        if (currUserTasks) {
            const tasks: ICompliance[] = currUserTasks
            if (tasks.length) tasks.sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())

            const { icon, title } = getTaskInfo(currentUserId, tasks)

            return <TasksList tasks={tasks} icon={icon} title={title} key={currentUserId} />
        }
    }

    const getOthersTasks = () => {
        if (othersTasksTotal > 0) {
            const value = t('tasksApprovals.tasks.directs_count', { count: othersTasksTotal })
            return (
                <AccordionRoot border="0" pt="0" pb="16px">
                    <AccordionItem border="0" value={value}>
                        <AccordionItemTrigger p="0" _hover={{ backgroundColor: 'transparent' }}>
                            <Flex alignItems="center">
                                <Icon path={mdiAccountSupervisor} size="16px" color="var(--chakra-colors-gray-500)" />
                                <Text
                                    pl="8px"
                                    fontSize="14px"
                                    fontWeight="bold"
                                    lineHeight="20px"
                                    color={textColor}
                                    textTransform="uppercase"
                                >
                                    {value}
                                </Text>
                            </Flex>
                            {/* <AccordionIcon color={arrowColor} ml="12px" /> */}
                        </AccordionItemTrigger>
                        <AccordionItemContent p="0" pt="16px">
                            {Object.keys(othersTasks).map((mudId, idx) => {
                                const tasks: ICompliance[] = othersTasks[mudId]
                                if (tasks.length) tasks.sort((a, b) => new Date(a.due_date).getTime() - new Date(b.due_date).getTime())

                                const { icon, title } = getTaskInfo(mudId, tasks)

                                return (
                                    <TasksList
                                        tasks={tasks}
                                        icon={icon}
                                        title={title}
                                        key={mudId}
                                        isDirect={true}
                                        isLastItem={idx === Object.keys(othersTasks).length - 1}
                                    />
                                )
                            })}
                        </AccordionItemContent>
                    </AccordionItem>
                </AccordionRoot>
            )
        }
    }

    return (
        <>
            {getCurrUserTasks()}
            {getOthersTasks()}
        </>
    )
}

export default Container
